// Základní HTTP klient pro API serveru.
// V dev módu jde přes vite proxy (/api -> http://localhost:3211), viz vite.config.js.
// Automaticky přikládá Bearer token z localStorage (klíč 'cw_auth_token').
const BASE_URL = import.meta.env.VITE_API_URL || '/api';

function getAuthHeader() {
  const token = localStorage.getItem('cw_auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Strop na jeden request. Bez něj visí zaseknutý fetch (vyčerpaný pool spojení na
// serveru, spadlá síť) neomezeně dlouho — a protože se odpovědi skládají přes
// Promise.all, jeden takový request drží spinner celé tabulky navěky. S timeoutem
// request skončí chybou, kterou React Query ukáže jako chybový stav.
// Čtení (GET) má krátký strop — když server do minuty neodpoví, je něco rozbité.
const DEFAULT_TIMEOUT_MS = 60000;

// Dotahování (POST /…/fill) tahá data z Oracle a běží dlouho legitimně, takže má
// vlastní, výrazně vyšší strop. Nula = bez timeoutu (lze předat v options).
const FILL_TIMEOUT_MS = 10 * 60 * 1000;

// POST na fill endpointy se pozná podle cesty — ať volající nemusí timeout hlídat sám.
const isFillPath = (path) => /\/fill(\?|$)/.test(path);

// Vypršení přihlášení — server u requestu s neplatným/expirovaným tokenem vrací
// 401 { error: 'session_expired' }. Handler registruje useAuthStore (tady nejde
// importovat store kvůli cyklické závislosti store → api/auth → client).
let sessionExpiredHandler = null;
export function setSessionExpiredHandler(fn) {
  sessionExpiredHandler = fn;
}

function isSessionExpired(status, body) {
  return status === 401 && body?.error === 'session_expired';
}

function notifySessionExpired(message) {
  // Jen pokud je token pořád uložený — souběžné requesty tak handler spustí jednou.
  if (sessionExpiredHandler && localStorage.getItem('cw_auth_token')) sessionExpiredHandler(message);
}

// Pro přímá fetch() volání mimo `api` (grafy s vlastní Bearer hlavičkou) — zkontroluje
// neúspěšnou odpověď a případně ohlásí vypršení přihlášení. Tělo čte z klonu.
export function checkSessionExpired(res) {
  if (res.status !== 401) return;
  res.clone().json()
    .then(body => { if (isSessionExpired(res.status, body)) notifySessionExpired(body.message); })
    .catch(() => {});
}

async function request(path, options = {}) {
  const { timeout = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  // AbortSignal.timeout hlídá celý request; signal z options (pokud ho volající dal)
  // má přednost, ať se nepřebíjí explicitní zrušení.
  const signal = fetchOptions.signal
    ?? (timeout > 0 ? AbortSignal.timeout(timeout) : undefined);

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeader(), ...fetchOptions.headers },
      ...fetchOptions,
      signal,
    });
  } catch (e) {
    // TimeoutError/AbortError přeložit na hlášku, která něco říká — hlášku
    // "signal is aborted without reason" uživatel dešifrovat nemůže.
    if (e?.name === 'TimeoutError' || e?.name === 'AbortError') {
      const err = new Error(`Server neodpověděl do ${Math.round(timeout / 1000)} s`);
      err.status = 0;
      err.error = 'timeout';
      throw err;
    }
    throw e;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Server posílá u chyb { error, message?, field? } — např. auth (account_pending,
    // password_change_required) nebo registrace (field = 'username' | 'email').
    // `message` je uživatelsky formulovaná hláška, `error` strojový kód.
    const err = new Error(body.message || body.error || `API error: ${res.status}`);
    err.status = res.status;
    err.error  = body.error;
    err.field  = body.field;
    err.body   = body;
    if (isSessionExpired(res.status, body)) notifySessionExpired(err.message);
    throw err;
  }
  return res.json();
}

export const api = {
  get:    (path, options)       => request(path, options),
  post:   (path, body, options) => request(path, {
    method: 'POST',
    body: JSON.stringify(body),
    timeout: isFillPath(path) ? FILL_TIMEOUT_MS : DEFAULT_TIMEOUT_MS,
    ...options,
  }),
  put:    (path, body, options) => request(path, { method: 'PUT', body: JSON.stringify(body), ...options }),
  delete: (path, options)       => request(path, { method: 'DELETE', ...options }),
};
